import { DateTime } from "luxon";
import { getValueByPath } from "shared/app/VerificationTemplate/utils";
import { VerifiedItem } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified";
import {
  DocumentSource,
  IncomeField,
  ItemDocument,
  ProviderMap
} from "./types";
import { CombinedIncome } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import { getIncomeActualNetAndGrossDollars } from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils";
import { IncomeSourceProvider } from "shared/types";

export const parseDate = (value?: string | null) => {
  if (!value) return null;
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.startOf("day") : null;
};

export const inclusiveDaysBetween = (
  start: DateTime | null,
  end: DateTime | null
) => {
  if (!start || !end) return 0;
  const days = Math.round(end.diff(start, "days").days) + 1;
  return days > 0 ? days : 0;
};

const sortedValidDates = (dates: Array<DateTime | null>) =>
  dates
    .filter((date): date is DateTime => date !== null)
    .sort((a, b) => a.toMillis() - b.toMillis());

export const earliestDate = (dates: Array<DateTime | null>) =>
  sortedValidDates(dates)[0] ?? null;

export const latestDate = (dates: Array<DateTime | null>) => {
  const sorted = sortedValidDates(dates);
  return sorted[sorted.length - 1] ?? null;
};

export const getItemDocument = (item: VerifiedItem) =>
  getValueByPath<ItemDocument | undefined>(item, ["data", "document"]);

export const isFraudulentItem = (item: VerifiedItem) =>
  getValueByPath<boolean | undefined>(item, [
    "income_source_data",
    "additional_data",
    "is_fraudulent"
  ]) === true;

export const documentTotal = (
  document: DocumentSource | ItemDocument | undefined,
  field: IncomeField
) =>
  (field === "gross"
    ? document?.total_gross_pay_amount
    : document?.total_net_pay_amount) ?? 0;

export const getDisplayedMonthlyDollars = (
  income: CombinedIncome,
  field: IncomeField
) => {
  const { actualVerifiedGrossMonthlyDollars, actualVerifiedNetMonthlyDollars } =
    getIncomeActualNetAndGrossDollars(income);
  return field === "gross"
    ? actualVerifiedGrossMonthlyDollars
    : actualVerifiedNetMonthlyDollars;
};

/**
 * Paystub / W2 items carry the document provider directly.
 * Bank / payroll items don't, so we use report-level map instead.
 */
export const resolveProvider = (
  items: VerifiedItem[],
  providers: ProviderMap = {}
) =>
  (items
    .map((item) =>
      "provider" in item && item.provider
        ? item.provider
        : providers[item.income_source_id]
    )
    .find(Boolean) as IncomeSourceProvider | undefined) ?? null;
